import React, { useState } from "react";
import {
  List,
  DataTable,
  Pagination,
  ReferenceField,
  Show,
  TabbedShowLayout,
  Tab,
  NumberField,
  TextInput,
  useRecordContext,
  useListContext,
  Button,
  TopToolbar,
  ReferenceManyField,
  ColumnsButton,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { DetailsButton } from "../lib/detailsButton";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";
import { CustomDatetimeField } from "../lib/datetime";
import { AdditionRecommendationBox } from "../lib/additionRecommendationBox";
import { SectionHeader } from "../lib/sectionHeader";
import {
  AdditionConfirmationField,
  GenerateAddMethodText,
} from "../lib/additionConfirmationField";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { DownloadCSV } from "../lib/csvDownload";
import { ResetExpandedRows } from "../lib/resetExpandedRows";
import { LotNameField } from "./lots";

// MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";

const LotAdditionAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {"Lot " + record.lotName + " Addition"}</span>;
};

const lotAdditionFilters = [
  <TextInput source="lotName" label="Lot" alwaysOn resettable />,
];

const LotAdditionExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Lot_Additions",
        custInfo.customerID,
        "events/lot_addition",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LotAdditionListActions = () => {
  return (
    <TopToolbar>
      <LotAdditionExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

function confirmationSummaryText(simplified, summary) {
  const unit =
    summary.unit === "tablet" && summary.quantity > 1
      ? "tablets"
      : summary.unit;

  let text;
  if (simplified) {
    text = `${Math.round(summary.quantity)} ${unit}`;
  } else {
    const s = GenerateAddMethodText(summary);
    text = `${summary.quantity} ${unit} ${s}`;
  }

  return text;
}

const ConfirmationSummary = ({ simplified }: any) => {
  const record = useRecordContext();

  if (
    !record ||
    !record.confirmationSummary ||
    record.confirmationSummary.length === 0
  ) {
    return null;
  }

  return (
    <>
      {record.confirmationSummary.map((summary) => (
        <div>
          <Typography variant="body2" component="span" noWrap={true}>
            {confirmationSummaryText(simplified, summary)}
          </Typography>
        </div>
      ))}
    </>
  );
};

// Shown when a lot addition row is expanded: the per-vessel additions the
// lot addition was exploded into.
export const LotAdditionAdditionsPanel = (props: any) => {
  const record = useRecordContext(props);
  if (!record) return null;

  return (
    <ReferenceManyField
      reference="events/sulfate_addition"
      target="lotAdditionID"
      record={record}
    >
      <DataTable
        bulkActionButtons={false}
        rowClick={false}
        sx={{ marginLeft: "4em", width: "fit-content", maxWidth: "100%" }}
      >
        <DataTable.Col source="barrelID" label="Vessel" disableSort>
          <AliasOrBarcodeReference
            source="barrelID"
            reference="vessels"
            sortable={false}
          />
        </DataTable.Col>
        <DataTable.Col source="addition" label="Addition (ppm)" disableSort>
          <NumberField source="addition" label="Addition (ppm)" />
        </DataTable.Col>
        <DataTable.Col source="datetime" label="Time" disableSort>
          <CustomDatetimeField source="datetime" label="Time" />
        </DataTable.Col>
        <DataTable.Col source="notes" label="Notes" disableSort>
          <ChatBubbleTooltip source="notes" />
        </DataTable.Col>
        <DataTable.Col disableSort>
          <DetailsButton />
        </DataTable.Col>
      </DataTable>
    </ReferenceManyField>
  );
};

export const LotAdditionList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={lotAdditionFilters}
    actions={<LotAdditionListActions />}
    sort={{ field: "timestamp", order: "DESC" }}
    storeKey={false}
    empty={false}
  >
    <ResetExpandedRows />
    <DataTable
      bulkActionButtons={false}
      rowClick={false}
      expand={<LotAdditionAdditionsPanel />}
    >
      <DataTable.Col source="lotID" label="Lot">
        <ReferenceField source="lotID" reference="lots" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="timestamp" label="Start Time">
        <CustomDatetimeField source="timestamp" label="Start Time" />
      </DataTable.Col>
      <DataTable.Col source="endTime" label="End Time" disableSort>
        <CustomDatetimeField source="endTime" label="End Time" />
      </DataTable.Col>
      <DataTable.Col source="barrelTreatedCount" label="Vessels">
        <NumberField label="Vessels" source="barrelTreatedCount" />
      </DataTable.Col>
      <DataTable.Col source="avgAddPerBarrel" label="Avg. Add per Barrel (ppm)">
        <NumberField
          source="avgAddPerBarrel"
          label={"Avg. Add per Barrel (ppm)"}
          options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
        />
      </DataTable.Col>
      <DataTable.Col
        source="confirmationSummary"
        label="Addition Summary"
        disableSort
      >
        <ConfirmationSummary simplified={true} />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const LotAdditionAddList = () => (
  <>
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="barrelID" label="Vessel" disableSort>
        <AliasOrBarcodeReference
          source="barrelID"
          reference="vessels"
          sortable={false}
        />
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Lot" disableSort>
        <ReferenceField
          source="lotID"
          reference="lots"
          link="show"
          sortable={false}
        >
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="addition" label="Addition (ppm)" disableSort>
        <NumberField source="addition" label="Addition (ppm)" />
      </DataTable.Col>
      <DataTable.Col source="datetime" label="Time" disableSort>
        <CustomDatetimeField source="datetime" label="Time" />
      </DataTable.Col>
      <DataTable.Col source="notes" label="Notes" disableSort>
        <ChatBubbleTooltip source="notes" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
    <Pagination rowsPerPageOptions={[]} />
  </>
);

export const LotAdditionShow = () => (
  <Show title={<LotAdditionAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <ReferenceField
          label="Lot"
          source="lotID"
          reference="lots"
          sortable={false}
          link="show"
        >
          <LotNameField />
        </ReferenceField>
        <CustomDatetimeField source="timestamp" label="Start Time" />
        <CustomDatetimeField source="endTime" label="End Time" />

        <SectionHeader title="Recommendation" />
        <AdditionRecommendationBox source="recommendation" />

        <SectionHeader title="Initial Lot Addition" />
        <NumberField
          source="confirmation.barrelCount"
          label="Number of Barrels"
          emptyText={"\u00A0"}
        />
        <NumberField
          source="addition"
          label="Initial Addition (ppm)"
          emptyText={"\u00A0"}
        />
        <AdditionConfirmationField label="Confirmation" />

        <SectionHeader title="Lot Addition Summary" />
        <NumberField label="Barrels" source="barrelTreatedCount" />
        <NumberField
          source="avgAddPerBarrel"
          label={"Avg. Add per Barrel (ppm)"}
          options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
        />
        <ConfirmationSummary label="Lot Addition Sum" simplified={false} />
      </Tab>
      <Tab label="Additions">
        <ReferenceManyField
          reference="events/sulfate_addition"
          target="lotAdditionID"
          label=""
        >
          <LotAdditionAddList />
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);
